import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, ArrowLeft, Send, Feather, Globe, Lock, Trash2, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import MultilingualText from "@/components/ui/MultilingualText";
import PhotoDropzone from "@/components/ui/PhotoDropzone";
import StoryCard from "@/components/app/StoryCard";
import { packMl, hasMlContent, mlPrimary } from "@/lib/ml";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";

/**
 * /app/memorial/:personId
 * A themed memorial page — lit candles + guestbook — for one person on the tree.
 * If no memorial exists yet, the "opener" can open one (idempotent).
 */
export default function MemorialPage() {
  const { personId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [person, setPerson] = useState(null);
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candleNote, setCandleNote] = useState("");
  const [candling, setCandling] = useState(false);
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const [opening, setOpening] = useState(false);

  // Disaster links (owner/admin managed)
  const [disasters, setDisasters] = useState([]);
  const [allDisasters, setAllDisasters] = useState([]);
  const [pickDisaster, setPickDisaster] = useState("");
  const [linkingDisaster, setLinkingDisaster] = useState(false);

  // Life stories
  const [stories, setStories] = useState([]);
  const [storyForm, setStoryForm] = useState(false);
  const [storyId, setStoryId] = useState(null);
  const [storyTitle, setStoryTitle] = useState("");
  const [storyBody, setStoryBody] = useState("");
  const [storyPhotos, setStoryPhotos] = useState([]);
  const [savingStory, setSavingStory] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, mRes, sRes] = await Promise.all([
        api.get(`/v1/persons/${personId}`),
        api.get(`/v1/memorials/by-person/${personId}`),
        api.get(`/v1/stories`, { params: { person_id: personId } }),
      ]);
      setPerson(pRes.data.person);
      setMemorial(mRes.data.memorial);
      setDisasters(mRes.data.disasters || []);
      setStories(sRes.data.stories || []);
      if (mRes.data.memorial) {
        try {
          const dRes = await api.get("/v1/disasters", { params: { limit: 60 } });
          setAllDisasters(dRes.data.disasters || []);
        } catch {
          /* picker stays empty */
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [personId]);
  useEffect(() => {
    load();
  }, [load]);

  async function openMemorial() {
    setOpening(true);
    try {
      const { data } = await api.post("/v1/memorials", { person_id: personId });
      setMemorial(data.memorial);
      toast.success(data.existed ? "Memorial was already open." : "Memorial opened.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOpening(false);
    }
  }

  async function lightCandle() {
    if (!memorial) return;
    setCandling(true);
    try {
      const { data } = await api.post("/v1/memorials/candles", {
        memorial_id: memorial.id,
        note: packMl(candleNote) || undefined,
      });
      setMemorial((m) => ({ ...m, candles: [...(m.candles || []), data.candle] }));
      setCandleNote("");
      toast.success("A candle is lit.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCandling(false);
    }
  }

  async function linkDisaster() {
    if (!memorial || !pickDisaster) return;
    setLinkingDisaster(true);
    try {
      const { data } = await api.post(`/v1/memorials/${memorial.id}/disaster-links`, { disaster_id: pickDisaster });
      setMemorial(data.memorial);
      setDisasters(data.disasters || []);
      setPickDisaster("");
      toast.success("Memorial connected to the disaster record.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLinkingDisaster(false);
    }
  }

  async function unlinkDisaster(disasterId) {
    if (!memorial) return;
    try {
      const { data } = await api.delete(`/v1/memorials/${memorial.id}/disaster-links/${disasterId}`);
      setMemorial(data.memorial);
      setDisasters(data.disasters || []);
      toast.success("Connection removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function togglePublish() {    if (!memorial) return;
    try {
      const { data } = await api.post("/v1/memorials/publish", {
        memorial_id: memorial.id,
        is_public: !memorial.is_public,
      });
      setMemorial(data.memorial);
      toast.success(
        data.memorial.is_public
          ? "This memorial is now public — anyone can find it."
          : "This memorial is now private to the family."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function postMessage(e) {
    e.preventDefault();
    if (!memorial || !hasMlContent(message)) return;
    setPosting(true);
    try {
      const { data } = await api.post("/v1/memorials/messages", {
        memorial_id: memorial.id,
        message: packMl(message),
      });
      setMemorial((m) => ({ ...m, messages: [...(m.messages || []), data.message] }));
      setMessage("");
      toast.success("Left in the guestbook.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center text-[color:var(--ink-soft)]">
        Opening the archive…
      </div>
    );
  }

  function resetStoryForm() {
    setStoryForm(false);
    setStoryId(null);
    setStoryTitle("");
    setStoryBody("");
    setStoryPhotos([]);
  }

  function startEditStory(s) {
    setStoryId(s.id);
    setStoryTitle(s.title || "");
    setStoryBody(s.body || "");
    setStoryPhotos(s.photos || []);
    setStoryForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function saveStory(e) {
    e.preventDefault();
    if (!hasMlContent(storyTitle) || !hasMlContent(storyBody)) {
      toast.error("A story needs a title and some words.");
      return;
    }
    setSavingStory(true);
    try {
      const payload = { title: packMl(storyTitle), body: packMl(storyBody), photos: storyPhotos };
      if (storyId) {
        const { data } = await api.patch(`/v1/stories/${storyId}`, payload);
        setStories((ss) => ss.map((s) => (s.id === storyId ? data.story : s)));
        toast.success("Story revised.");
      } else {
        const { data } = await api.post("/v1/stories", { ...payload, person_id: personId });
        setStories((ss) => [data.story, ...ss]);
        toast.success("Their story is told.");
      }
      resetStoryForm();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingStory(false);
    }
  }

  async function deleteStory(id) {
    if (!confirm("Remove this story from the memorial?")) return;
    try {
      await api.delete(`/v1/stories/${id}`);
      setStories((ss) => ss.filter((s) => s.id !== id));
      toast.success("Story removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  if (!person) return null;

  const fullName = [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(" ");
  const years = [person.birth_date, person.death_date].filter(Boolean).join(" – ");

  return (
    <section className="mx-auto max-w-4xl px-6 md:px-10 pt-8 pb-24">
      <button
        onClick={() => nav(`/app/family/${person.family_id}`)}
        data-testid="memorial-back"
        className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-6"
      >
        <ArrowLeft size={12} /> Back to the family
      </button>

      {/* Title plate */}
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="text-center"
      >
        <div className="overline">In loving memory of</div>
        <h1
          data-testid="memorial-name"
          className="heritage-title mt-2 leading-[1.02] tracking-[-0.01em]"
          style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
        >
          {fullName || "Unnamed"}
        </h1>
        {years && (
          <div className="mt-3 font-italic-serif italic text-lg text-[color:var(--sepia)]">
            {years}
          </div>
        )}
        {person.birth_place && (
          <div className="mt-1 numeric-index">{person.birth_place}</div>
        )}
      </motion.header>

      {/* Portrait / bio */}
      {(person.photo_url || person.bio) && (
        <div className="mt-10 grid md:grid-cols-2 gap-8 items-start">
          {person.photo_url && (
            <div className="vintage-frame max-w-xs mx-auto">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={fileUrl(person.photo_url)} alt={fullName} />
              </div>
            </div>
          )}
          {person.bio && (
            <MultilingualText
              value={person.bio}
              className="font-italic-serif italic text-lg leading-[1.65] text-[color:var(--ink-soft)] max-w-[52ch]"
              testid="memorial-bio"
            />
          )}
        </div>
      )}

      {/* Life stories — long-form, multi-photo, multilingual */}
      <section className="mt-16" data-testid="stories-section">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl">Life stories</h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>

        {stories.length === 0 && !storyForm && (
          <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)] mb-6">
            No stories yet — the small, true ones matter most.
          </p>
        )}
        <div className="space-y-8">
          {stories.map((s) => (
            <StoryCard
              key={s.id}
              story={s}
              canEdit={s.created_by === user?.id || user?.role === "admin"}
              onEdit={() => startEditStory(s)}
              onDelete={() => deleteStory(s.id)}
            />
          ))}
        </div>

        {storyForm ? (
          <form
            onSubmit={saveStory}
            data-testid="story-form"
            className="mt-8 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 md:p-8 shadow-[0_16px_44px_-24px_rgba(43,28,15,0.4)]"
          >
            <div className="overline mb-5">{storyId ? "Revise this story" : "Tell their story"}</div>
            <MultilingualEditor
              as="input"
              testid="story-title"
              label="Title"
              value={storyTitle}
              onChange={setStoryTitle}
              maxLength={200}
              placeholder="The winter she walked to school…"
            />
            <div className="mt-5">
              <MultilingualEditor
                as="textarea"
                rows={8}
                testid="story-body"
                label="The story"
                value={storyBody}
                onChange={setStoryBody}
                maxLength={12000}
                placeholder="Take your time. The details are the gold…"
              />
            </div>
            <div className="mt-6">
              <div className="overline mb-2">Photographs</div>
              {storyPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-3" data-testid="story-photo-list">
                  {storyPhotos.map((p, i) => (
                    <div key={i} className="relative border border-[color:var(--sepia)]/25">
                      <img src={fileUrl(p)} alt="" className="w-full aspect-[4/3] object-cover" />
                      <button
                        type="button"
                        data-testid={`story-photo-remove-${i}`}
                        onClick={() => setStoryPhotos((ps) => ps.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 bg-[color:var(--parchment)] border border-[color:var(--sepia)]/40 p-1 hover:text-[color:var(--rust)]"
                        title="Remove"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {storyPhotos.length < 12 && (
                <PhotoDropzone
                  testid="story-photo"
                  value=""
                  onChange={(url) => url && setStoryPhotos((ps) => [...ps, url])}
                />
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                data-testid="story-save"
                disabled={savingStory || !hasMlContent(storyTitle) || !hasMlContent(storyBody)}
                className="btn-wood disabled:opacity-50"
              >
                <Send size={14} /> {savingStory ? "Saving…" : storyId ? "Save changes" : "Add to the memorial"}
              </button>
              <button type="button" onClick={resetStoryForm} data-testid="story-cancel" className="btn-ghost">
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-8 text-center">
            <button
              data-testid="story-new"
              onClick={() => setStoryForm(true)}
              className="btn-ghost !py-2.5"
            >
              <Feather size={14} /> Write their story
            </button>
          </div>
        )}
      </section>

      {!memorial ? (
        // Open state
        <div className="mt-14 border border-[color:var(--sepia)]/25 p-8 md:p-12 text-center bg-[color:var(--parchment)]">
          <Flame size={28} className="mx-auto text-[color:var(--rust)]" />
          <h2 className="mt-3 heritage-title text-3xl">Open a memorial</h2>
          <p className="mt-2 max-w-md mx-auto text-[color:var(--ink-soft)]">
            A candle-lit page that any family member can visit — to leave a note,
            light a candle, and remember together.
          </p>
          <button
            data-testid="memorial-open-btn"
            disabled={opening}
            onClick={openMemorial}
            className="btn-wood mt-6 disabled:opacity-60"
          >
            <Feather size={14} /> {opening ? "Opening…" : "Open memorial page"}
          </button>
        </div>
      ) : (
        <>
          {/* Candles */}
          <section className="mt-14" data-testid="memorial-candles-section">
            {/* Public / private toggle */}
            <div className="mb-8 flex items-center justify-between border border-[color:var(--sepia)]/25 bg-[color:var(--parchment-2)]/50 px-5 py-3">
              <div className="flex items-center gap-3 text-sm">
                {memorial.is_public ? (
                  <>
                    <Globe size={16} className="text-[color:var(--rust)]" />
                    <div>
                      <div className="font-medium">Public memorial</div>
                      <div className="text-xs text-[color:var(--ink-soft)]">
                        Anyone in the world can find this page by name and country.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Lock size={16} className="text-[color:var(--sepia)]" />
                    <div>
                      <div className="font-medium">Private to the family</div>
                      <div className="text-xs text-[color:var(--ink-soft)]">
                        Only family members can view or contribute.
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                data-testid="memorial-toggle-publish"
                onClick={togglePublish}
                className="btn-ghost !py-1.5 !px-4 !text-xs"
              >
                {memorial.is_public ? "Make private" : "Publish worldwide"}
              </button>
            </div>

            {/* Connected disasters — memorial owner or admin */}
            {(user?.role === "admin" || memorial.opened_by === user?.id) && (
              <div className="mb-8 border border-[color:var(--sepia)]/25 bg-[color:var(--parchment-2)]/50 px-5 py-4" data-testid="memorial-disaster-links">
                <div className="overline text-[color:var(--sepia)] mb-2 flex items-center gap-2">
                  <Link2 size={12} /> Connected disaster records
                </div>
                {disasters.length > 0 && (
                  <ul className="mb-3 space-y-1.5">
                    {disasters.map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`memorial-disaster-${d.id}`}>
                        <span>
                          {d.name} <span className="text-[color:var(--ink-soft)]">· {d.country} · {d.status}</span>
                        </span>
                        <button
                          onClick={() => unlinkDisaster(d.id)}
                          data-testid={`memorial-disaster-unlink-${d.id}`}
                          className="text-[10px] link-underline text-[color:var(--ink-soft)] hover:text-[color:var(--rust)] shrink-0"
                        >
                          remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-[color:var(--ink-soft)] mb-3">
                  If their passing is connected to a documented disaster, linking places this memorial
                  in the disaster's In Memoriam record (visible publicly once the memorial is published).
                </p>
                <div className="flex gap-2">
                  <select
                    value={pickDisaster}
                    onChange={(e) => setPickDisaster(e.target.value)}
                    data-testid="memorial-disaster-select"
                    className="flex-1 bg-transparent border-b border-[color:var(--sepia)]/50 py-1.5 text-sm focus:outline-none focus:border-[color:var(--rust)]"
                  >
                    <option value="">Choose a disaster record…</option>
                    {allDisasters
                      .filter((d) => !disasters.some((x) => x.id === d.id))
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name} — {d.country}
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={linkDisaster}
                    disabled={!pickDisaster || linkingDisaster}
                    data-testid="memorial-disaster-link-btn"
                    className="btn-ghost !py-1.5 !px-4 !text-xs disabled:opacity-50"
                  >
                    Link
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
              <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
                <Flame size={20} className="text-[color:var(--rust)]" />
                Candles lit ·{" "}
                <span className="tabular-nums">{(memorial.candles || []).length}</span>
              </h2>
              <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            </div>

            {/* Grid of candles */}
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-3 mb-8">
              <AnimatePresence>
                {(memorial.candles || []).map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center"
                    title={mlPrimary(c.note) || c.name}
                  >
                    <Flame
                      size={26}
                      className="mx-auto text-[color:var(--rust)] drop-shadow-[0_0_10px_rgba(156,66,33,0.55)]"
                    />
                    <div className="mt-1 text-[10px] text-[color:var(--ink-soft)] truncate">
                      {c.name.split(" ")[0]}
                    </div>
                    {c.note && (
                      <MultilingualText
                        value={c.note}
                        quoted
                        className="mt-1 text-[11px] leading-snug font-italic-serif italic text-[color:var(--sepia)] line-clamp-3"
                      />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Light one */}
            <div className="border-l-2 border-[color:var(--rust)] pl-4">
              <div>
                <span className="overline block mb-1">A note (optional)</span>
                <MultilingualEditor
                  as="input"
                  testid="memorial-candle-note"
                  value={candleNote}
                  onChange={setCandleNote}
                  maxLength={140}
                  placeholder="A whispered goodbye…"
                />
              </div>
              <button
                data-testid="memorial-light-candle"
                onClick={lightCandle}
                disabled={candling}
                className="btn-wood mt-4 disabled:opacity-60"
              >
                <Flame size={14} /> {candling ? "Lighting…" : "Light a candle"}
              </button>
            </div>
          </section>

          {/* Guestbook */}
          <section className="mt-16" data-testid="memorial-guestbook-section">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
              <h2 className="heritage-title text-2xl md:text-3xl">
                Family guestbook
              </h2>
              <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            </div>

            <div className="space-y-6">
              {(memorial.messages || []).length === 0 && (
                <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)]">
                  The first page is always the hardest. Leave a memory below.
                </p>
              )}
              {(memorial.messages || []).map((m, i) => (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.5 }}
                  className="border-l-2 border-[color:var(--sepia)]/60 pl-4 py-1"
                >
                  <div className="overline text-[color:var(--sepia)]">
                    {m.name} ·{" "}
                    {new Date(m.created_at).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                  <MultilingualText
                    value={m.message}
                    quoted
                    className="mt-1 font-italic-serif italic text-lg leading-snug"
                  />
                </motion.article>
              ))}
            </div>

            <form onSubmit={postMessage} className="mt-10" data-testid="memorial-guestbook-form">
              <div>
                <span className="overline block mb-1">Your remembrance</span>
                <MultilingualEditor
                  as="textarea"
                  rows={3}
                  testid="memorial-message"
                  value={message}
                  onChange={setMessage}
                  maxLength={1200}
                  placeholder="A few lines they would want to read…"
                />
              </div>
              <button
                data-testid="memorial-post-message"
                disabled={posting || !hasMlContent(message)}
                type="submit"
                className="btn-wood mt-4 disabled:opacity-50"
              >
                <Send size={14} /> {posting ? "Sending…" : "Leave in the guestbook"}
              </button>
            </form>
          </section>
        </>
      )}

      <div className="mt-16">
        <div className="flourish w-64 mx-auto" />
      </div>
    </section>
  );
}
